import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ProjectGroup, ProjectUser } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { removeFromArray } from '@reflact/tsutil';



export type MongoUpdateResult = {
  acknowledged: boolean;
  matchedCount: number;
  modifiedCount: number;
  upsertedCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class UserPermissionAdminService {


  private _apiReadUrl = "/api/admin/project/userpermission/read";
  private _apiWriteUrl = "/api/admin/project/userpermission/write";


  public projectUsers: ProjectUser[] = [];
  public projectGroups: ProjectGroup[] = [];

  public botUsers: ProjectUser[] = [];
  public botGroups: ProjectGroup[] = [];

  public groupUsers: ProjectUser[] = [];

  public selectableBotUsers: ProjectUser[] = [];
  public selectableBotGroups: ProjectGroup[] = [];
  public selectableGroupUsers: ProjectUser[] = [];

  private _selectedProjectId: string = "";
  private _selectedBotId: string = "";
  private _selectedGroupId: string = "";



  constructor(private http: HttpClient) {
  }

  public get selectetedGroup() {
    return this.projectGroups.find(pg => pg._id == this.selectedGroupId);
  }

  private updateSelectableBotUsersAndGroups() {
    this.selectableBotUsers = this.projectUsers.filter(u => this.botUsers.find(bu => bu._id == u._id) == null);
    this.sortUserArray(this.selectableBotUsers);

    this.selectableBotGroups = this.projectGroups.filter(u => this.botGroups.find(bu => bu._id == u._id) == null);
    this.sortProjectGroupArray(this.selectableBotGroups);

  }
  private updateSelectableGroupUsers() {
    this.selectableGroupUsers = this.projectUsers.filter(u => this.groupUsers.find(bu => bu._id == u._id) == null);
    this.sortUserArray(this.selectableGroupUsers);

  }

  public get selectedProjectId(): string {
    return this._selectedProjectId;
  }

  public set selectedProjectId(p: string) {
    if (this._selectedProjectId == p) { return; }
    this._selectedProjectId = p;
    void this.selectedProjectChanged();
  }
  private async selectedProjectChanged() {
    if (this.selectedProjectId == "" || this.selectedProjectId === '-') {
      this.projectGroups = [];
      this.projectUsers = [];
      this.botGroups = [];
      this.botUsers = [];
      this.selectableBotUsers = [];
      this.selectableBotGroups = [];
      this.groupUsers = [];
      this.selectedGroupId = "";
      this.selectedBotId = "";
    }
    const usersAndGroups = await this.getUserAndGoupList();
    this.projectGroups = usersAndGroups.groups;
    this.sortProjectGroupArray(this.projectGroups);
    this.projectUsers = usersAndGroups.users;
    this.sortUserArray(this.projectUsers);

    if (this.selectedBotId != "") {
      void this.selectedBotIdChanged();
    }
    if (this.selectedGroupId != "") {
      void this.selectedGroupChanged();
    }
  }

  public get selectedBotId(): string {
    return this._selectedBotId;
  }

  public set selectedBotId(id: string) {
    if (this._selectedBotId == id) { return; }
    this._selectedBotId = id;
    void this.selectedBotIdChanged();

  }
  private async selectedBotIdChanged() {
    if (this.selectedBotId == "") {
      this.botGroups = [];
      this.botUsers = [];
      this.selectedBotId = "";
      this.updateSelectableBotUsersAndGroups();
      return;
    }
    if (this.selectedProjectId == "" || this.selectedProjectId === '-') {
      this.updateSelectableBotUsersAndGroups();
      return;
    }
    const usersAndGroups = await this.getBotUserAndGroupList();
    this.botGroups = usersAndGroups.groups;
    this.sortProjectGroupArray(this.botGroups);
    this.botUsers = usersAndGroups.users;
    this.sortUserArray(this.botUsers);
    this.updateSelectableBotUsersAndGroups();
  }


  public get selectedGroupId(): string {
    return this._selectedGroupId;
  }

  public set selectedGroupId(id: string) {
    if (this._selectedGroupId == id) { return; }
    this._selectedGroupId = id;
    void this.selectedGroupChanged();
  }

  private async selectedGroupChanged() {
    if (this.selectedGroupId == "") {
      this.groupUsers = [];
      this.updateSelectableGroupUsers();
      return;
    }
    if (this.selectedProjectId == "" || this.selectedProjectId === '-') {
      this.updateSelectableGroupUsers();
      return;
    }
    const groupUsers = await this.getUsersInGroup();
    this.groupUsers = groupUsers;
    this.sortProjectGroupArray(this.groupUsers);
    this.updateSelectableGroupUsers();
  }


  public async getUserAndGoupList() {
    if (this.selectedProjectId == "" || this.selectedProjectId === '-') {
      return {
        users: [], groups: []
      };
    }
    const result = await firstValueFrom(this.http.get<{
      users: ProjectUser[]
      groups: ProjectGroup[]
    }>(`${this._apiReadUrl}/${this.selectedProjectId}/list`));
    return result;
  }

  private sortUserArray(projectUsers: ProjectUser[]) {
    projectUsers.sort((a, b) => a.name.localeCompare(b.name));
  }
  private sortProjectGroupArray(projectGroups: ProjectGroup[]) {
    projectGroups.sort((a, b) => a.name.localeCompare(b.name));
  }

  private async getUserList() {
    const result = await firstValueFrom(this.http.get<ProjectUser[]>(`${this._apiReadUrl}/${this.selectedProjectId}/user/list`));
    this.sortUserArray(result);
    return result;
  }

  private async getUserGroups() {
    const result = await firstValueFrom(this.http.get<ProjectGroup[]>(`${this._apiReadUrl}/${this.selectedProjectId}/group/list`));
    this.sortProjectGroupArray(result);
    return result;
  }

  private async getUsersInGroup() {
    const result = await firstValueFrom(this.http.get<ProjectUser[]>(`${this._apiReadUrl}/${this.selectedProjectId}/group/${this.selectedGroupId}/list`));
    this.sortProjectGroupArray(result);
    return result;
  }

  public async addUserToProject(name: string, email: string = "") {
    const result = await firstValueFrom(this.http.post<ProjectUser>(`${this._apiWriteUrl}/${this.selectedProjectId}/user/add`, { email, name }));
    if (this.selectedProjectId == result.projectId) {
      this.projectUsers.push(result);
      this.sortUserArray(this.projectUsers);
      this.updateSelectableBotUsersAndGroups();
      this.updateSelectableGroupUsers();

    }
    return result;
  }
  public async removeUsersFromProject(userIds: string[]) {
    const result = await firstValueFrom(this.http.post<ProjectUser[]>(`${this._apiWriteUrl}/${this.selectedProjectId}/user/remove`, { userIds }));
    if (this.selectedProjectId == result[0]?.projectId) {
      removeFromArray(this.projectUsers, (u => result.map(a => a._id).includes(u._id)));
      removeFromArray(this.groupUsers, (u => result.map(a => a._id).includes(u._id)));
      this.updateSelectableGroupUsers();
      void this.selectedBotIdChanged();
    }
    return result;
  }
  public async addGroupToProject(name: string) {
    const result = await firstValueFrom(this.http.post<ProjectGroup>(`${this._apiWriteUrl}/${this.selectedProjectId}/group/add`, { name }));
    if (this.selectedProjectId == result.projectId) {
      this.projectGroups.push(result);
      this.updateSelectableBotUsersAndGroups();
    }
    return result;
  }
  public async removeGroupsFromProject(groupIds: string[]) {
    const result = await firstValueFrom(this.http.post<ProjectGroup[]>(`${this._apiWriteUrl}/${this.selectedProjectId}/group/remove`, { groupIds }));
    if (this.selectedProjectId == result[0]?.projectId) {
      removeFromArray(this.projectGroups, (pg => result.map(rr => rr._id).includes(pg._id)));
      await this.selectedBotIdChanged();
    }
    return result;
  }
  public async assignUsersToGroup(userIds: string[]) {
    const result = await firstValueFrom(
      this.http.post<MongoUpdateResult & {
        groupId: string,
        userIds: string[],
        projectId: string
      }>(`${this._apiWriteUrl}/${this.selectedProjectId}/group/${this.selectedGroupId}/assign`, { userIds })
    );
    await this.selectedGroupChanged();
    this.updateSelectableGroupUsers();
    return result;
  }
  public async revokeUsersFromGroup(userIds: string[]) {
    const result = await firstValueFrom(
      this.http.post<MongoUpdateResult & {
        groupId: string,
        userIds: string[],
        projectId: string
      }>(`${this._apiWriteUrl}/${this.selectedProjectId}/group/${this.selectedGroupId}/revoke`, { userIds })
    );
    await this.selectedGroupChanged();
    this.updateSelectableGroupUsers();
    return result;
  }


  private async getBotUserAndGroupList() {
    const result = await firstValueFrom(this.http.get<{
      users: ProjectUser[]
      groups: ProjectGroup[]
    }>(`${this._apiReadUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/list`));
    return result;
  }


  public async getBotUserList() {
    const result = await firstValueFrom(this.http.get<ProjectUser[]>(
      `${this._apiReadUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/user/list`));
    return result;
  }

  public async getBotGroupList() {
    const result = await firstValueFrom(this.http.get<ProjectUser[]>(
      `${this._apiReadUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/group/list`));
    return result;
  }

  public async assignUsersToBot(userIds: string[]) {
    const result = await firstValueFrom(this.http.post<MongoUpdateResult & {
      userIds: string[]
      botId: string
      projectId: string
    }>(`${this._apiWriteUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/user/assign`,
      { userIds }
    ));
    this.botUsers = await this.getBotUserList();
    this.sortUserArray(this.botUsers);
    this.updateSelectableBotUsersAndGroups();
    return result;
  }
  public async revokeUsersFromBot(userIds: string[]) {
    const result = await firstValueFrom(this.http.post<MongoUpdateResult & {
      userIds: string[]
      botId: string
      projectId: string
    }>(`${this._apiWriteUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/user/revoke`,
      { userIds }
    ));
    this.botUsers = await this.getBotUserList();
    this.sortUserArray(this.botUsers);
    this.updateSelectableBotUsersAndGroups();

    return result;
  }

  public async assignGroupsToBot(groupIds: string[]) {
    const result = await firstValueFrom(this.http.post<MongoUpdateResult & {
      groupIds: string[]
      botId: string
      projectId: string
    }>(`${this._apiWriteUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/group/assign`,
      { groupIds }
    ));

    this.botGroups = await this.getBotGroupList();
    this.sortProjectGroupArray(this.botGroups);
    this.updateSelectableBotUsersAndGroups();
    return result;
  }

  public async revokeGroupsFromBot(groupIds: string[]) {
    const result = await firstValueFrom(this.http.post<MongoUpdateResult & {
      groupIds: string[]
      botId: string
      projectId: string
    }>(`${this._apiWriteUrl}/${this.selectedProjectId}/bot/${this.selectedBotId}/group/revoke`,
      { groupIds }
    ));
    this.botGroups = await this.getBotGroupList();
    this.sortProjectGroupArray(this.botGroups);
    this.updateSelectableBotUsersAndGroups();
    return result;
  }
}
